import React, { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Button, Icon } from "components/lib";

export default function ConfirmationModal({
                                              open,
                                              setOpen,
                                              title = "Are you sure?",
                                              message = "This action cannot be undone.",
                                              icon = "alert-triangle",
                                              iconColor = "red",
                                              confirmText = "Confirm",
                                              cancelText = "Cancel",
                                              onConfirm,
                                          }) {
    const cancelButtonRef = useRef(null);
    const [loading, setLoading] = useState(false);

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-10 inset-0 overflow-y-auto"
                initialFocus={cancelButtonRef}
                onClose={() => !loading && setOpen(false)}
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                    {/* Overlay */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    {/* Centering trick */}
                    <span
                        className="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                    >
            &#8203;
          </span>

                    {/* Modal Panel */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="relative inline-block align-bottom bg-white rounded-lg px-6 pt-6 pb-5 text-left overflow-hidden shadow-xl transform transition-all mt-12 sm:mt-16 sm:mb-8 sm:align-middle sm:max-w-md sm:w-full">

                            {/* Header */}
                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
                                    <Icon image={icon} size={20} color={iconColor} />
                                </div>

                                <Dialog.Title className="text-lg font-semibold text-gray-900">
                                    {title}
                                </Dialog.Title>
                            </div>

                            {/* Message */}
                            <div className="mt-2">
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {message}
                                </p>
                            </div>

                            {/* Buttons */}
                            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end items-center gap-4">

                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    disabled={loading}
                                    className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-150"
                                    ref={cancelButtonRef}
                                >
                                    {cancelText}
                                </button>

                                <Button
                                    text={loading ? "Processing..." : confirmText}
                                    action={async () => {
                                        if (!onConfirm) return;

                                        try {
                                            setLoading(true);
                                            await onConfirm();
                                            setOpen(false);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    className="bg-orange-500 hover:bg-orange-600 text-white"
                                    xSmall
                                    disabled={loading}
                                />

                            </div>

                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}